import uuid from "uuid-random";
import {
  ProgrammingRow,
  SlotClassroom,
  SlotInstructor,
} from "@services/types/programming-edit/programmingEditTypes";
import {
  ScheduleInstance,
  ProgrammingStructure,
  ProgrammingStructurePart,
  ProgrammingStructureStage,
  MicroprogrammingPreviewInput,
  MicroprogrammingPreviewSection,
  StageInstancesInput,
  MicroprogrammingInstanceInput,
  EditionMode,
  EditorOperation,
  SessionPayload,
} from "@models/ISchema";
import {
  ProgrammingSessionCrudInput,
  ProgrammingSessionCrudResult,
} from "@services/types/programming-edit/programmingEditTypes";
import { blocksToHours } from "@utils/blocks";
import { DAY_NAMES_ES, DAY_NUMBER } from "@utils/enums";
import { netUnassignedInstructorIds } from "@utils/unassignedInstructors";

/**
 * Flattens ScheduleInstance[] into ProgrammingRow[] for the store.
 * Each ScheduleInstance may have multiple sections, each with multiple sessions.
 * Each section+session pair on a given date becomes one table row.
 */
export const adaptSlotsToRows = (instances: ScheduleInstance[]): ProgrammingRow[] => {
  if (!instances?.length) return [];

  const rows: ProgrammingRow[] = [];

  for (const instance of instances) {
    const date = instance.date ?? "";
    const dayOfWeek = instance.day ?? "";
    const startTime = instance.startingTime ?? "";
    const endTime = instance.endingTime ?? "";

    for (const section of instance.sections ?? []) {
      const component = section?.component?.code ?? "";

      for (const session of section?.sessions ?? []) {
        if (!session?.id) continue;

        const durationBlocks = session.blocksCount ?? 0;
        const durationHours = blocksToHours(durationBlocks);
        const sessionBlocks = (session.blocks ?? []).filter(Boolean).map(b => ({
          id: b?.id ?? "",
          startingTime: b?.startingTime ?? "",
          endingTime: b?.endingTime ?? "",
        }));

        rows.push({
          id: `${date}_${startTime}_${endTime}_${session.id}`,
          date,
          dayOfWeek,
          startTime,
          endTime,
          durationBlocks,
          durationHours,
          component,
          stageId: null,
          instructors: (session.instructors ?? [])
            .filter(Boolean)
            .map(i => ({ id: i?.id ?? "", name: i?.name ?? "", code: i?.code ?? "" })),
          classrooms: (session.classrooms ?? [])
            .filter(Boolean)
            .map(c => ({ id: c?.id ?? "", name: c?.name ?? "", code: c?.code ?? "" })),
          splitPart: null,
          splitGroupId: null,
          isSelected: false,
          blocks: sessionBlocks,
          blocksIds: sessionBlocks.map(b => b.id),
          intervalId: instance.interval?.id ?? "",
          originSectionId: section?.id ?? "",
          originSessionId: session.id,
        });
      }
    }
  }

  return rows;
};

/**
 * Transforms API stages into the stage catalog for the inline Select in each table row.
 */
export interface AdaptedStage extends ProgrammingStructureStage {
  blockDuration: number;
  dependencyCodes: string[];
}

export const adaptStagesToOptions = (apiStages: any[]): AdaptedStage[] => {
  if (!apiStages?.length) return [];
  return apiStages.map(s => ({
    id: s.id,
    name: s.name ?? "",
    code: s.code ?? "",
    programmingStructureStageType: s.programmingStructureStageType ?? null,
    blockDuration: s.blockDuration ?? 0,
    dependencyCodes: s.dependencyCodes ?? [],
  }));
};

/**
 * Transforms ProgrammingStructurePart[] (from PROGRAMMING_STRUCTURES_QUERY)
 * into the AdaptedStage catalog used by the store and summary computation.
 */
const dayNameToId: Record<string, number> = {
  [DAY_NAMES_ES.MONDAY]: DAY_NUMBER.MONDAY,
  [DAY_NAMES_ES.TUESDAY]: DAY_NUMBER.TUESDAY,
  [DAY_NAMES_ES.WEDNESDAY]: DAY_NUMBER.WEDNESDAY,
  [DAY_NAMES_ES.THURSDAY]: DAY_NUMBER.THURSDAY,
  [DAY_NAMES_ES.FRIDAY]: DAY_NUMBER.FRIDAY,
  [DAY_NAMES_ES.SATURDAY]: DAY_NUMBER.SATURDAY,
  [DAY_NAMES_ES.SUNDAY]: DAY_NUMBER.SUNDAY,
};

/**
 * Builds the MicroprogrammingPreviewInput from the current store slots.
 * Groups slots by stageId, looks up stageCode/stageName from the stages catalog,
 * and derives dayId from the Spanish day name.
 */
export const adaptSlotsToPreviewInput = (
  slots: ProgrammingRow[],
  stages: AdaptedStage[],
  programmingStructureId: string,
): MicroprogrammingPreviewInput => {
  const stageById = new Map(stages.map(s => [s.id, s]));

  const slotsByStageId = new Map<string, ProgrammingRow[]>();
  for (const slot of slots) {
    if (!slot?.stageId) continue;

    const existing = slotsByStageId.get(slot?.stageId);

    if (existing) {
      existing.push(slot);
    } else {
      slotsByStageId.set(slot?.stageId, [slot]);
    }
  }

  const instancesByStage: StageInstancesInput[] = Array.from(slotsByStageId.entries()).reduce<
    StageInstancesInput[]
  >((acc, [stageId, stageSlots]) => {
    const stage = stageById.get(stageId);
    if (!stage) return acc;

    const instances: MicroprogrammingInstanceInput[] = stageSlots.map((slot: ProgrammingRow) => ({
      date: slot.date,
      day: slot.dayOfWeek,
      dayId: dayNameToId[slot.dayOfWeek] ?? undefined,
      startingTime: slot.startTime,
      endingTime: slot.endTime,
      blocksCount: slot.durationBlocks,
      blocksIds: slot.blocksIds,
      intervalId: slot.intervalId,
      originSectionId: slot.originSectionId || undefined,
      originSessionId: slot.originSessionId || undefined,
      classroomIds: slot.classrooms.map((c: SlotClassroom) => c.id),
      instructorIds: slot.instructors.map((i: SlotInstructor) => i.id),
    }));

    acc.push({
      stageCode: stage.code ?? "",
      stageName: stage.name ?? undefined,
      instances,
    });

    return acc;
  }, []);

  return { programmingStructureId, instancesByStage };
};

/**
 * Auto-assigns a stage to each slot whose component code matches (case-insensitive)
 * a stage code from the selected structure. Slots without a match keep `stageId: null`.
 * First-match-wins if two stages share the same code.
 */
export const autoAssignStagesToSlots = (
  slots: ProgrammingRow[],
  stages: AdaptedStage[],
): ProgrammingRow[] => {
  if (!slots?.length || !stages?.length) return slots;

  const stageByCode = new Map<string, AdaptedStage>();
  for (const stage of stages) {
    const code = stage?.code?.toLowerCase();
    if (code && !stageByCode.has(code)) stageByCode.set(code, stage);
  }

  return slots.map(slot => {
    const component = slot?.component?.toLowerCase();
    if (!component) return slot;
    const match = stageByCode.get(component);
    return match ? { ...slot, stageId: match.id } : slot;
  });
};

export const adaptPartsToStages = (parts: ProgrammingStructurePart[]): AdaptedStage[] => {
  if (!parts?.length) return [];
  return parts
    .filter(part => part?.programmingStructureStage)
    .map(part => ({
      id: part.programmingStructureStage?.id ?? "",
      name: part.programmingStructureStage?.name ?? "",
      code: part.programmingStructureStage?.code ?? "",
      programmingStructureStageType:
        part.programmingStructureStage?.programmingStructureStageType ?? null,
      blockDuration: part.blockDuration ?? 0,
      dependencyCodes: part.dependenciesCodes?.filter(Boolean) ?? [],
    }));
};

/**
 * Maps a ProgrammingStructure to the option shape consumed by the structure
 * selector (`StructureSelectOption`). Mirrors the option built in
 * `ProgrammingActionBar.loadOptions`, including `self` so the select's
 * `formatOptionLabel` can render the structure name/code for the pre-selected
 * value. Used to pre-select the structure already applied to the link.
 */
export const adaptStructureToSelectOption = (structure: ProgrammingStructure) => ({
  value: structure.id,
  label: `${structure.name} (${structure.code})`,
  self: structure,
});

export type StructureSelectOptionWithSelf = ReturnType<typeof adaptStructureToSelectOption>;

/**
 * Ensures the structure currently applied to the link is selectable from the
 * dropdown while it remains the link's persisted structure (CHS-2632, AC5).
 *
 * The backend may omit the applied structure from the paginated results for
 * several reasons (visibility, subject/campus filters, or simply landing on a
 * later page), so membership — not visibility — is the reliable signal. To be
 * robust to all of those, this helper:
 *   1. Strips the applied structure from the backend results on every page, so
 *      it can never appear twice.
 *   2. Re-pins it at the top of the first page (respecting the search term).
 *
 * The result: the applied structure appears exactly once, at the top, whether
 * or not the backend returned it and regardless of which page it would fall on.
 *
 * Anchored on the link's persisted structure, so after the user replaces it with
 * another structure and saves, the previous one is neither stripped-as-applied
 * nor re-pinned and disappears from the selector — exactly the AC5 restriction.
 */
export const injectAppliedStructureOption = (
  options: StructureSelectOptionWithSelf[],
  applied: ProgrammingStructure | null | undefined,
  { page, term }: { page: number; term: string },
): StructureSelectOptionWithSelf[] => {
  if (!applied) return options;

  // Remove any natural occurrence to avoid duplicating it across pages.
  const withoutApplied = options.filter(option => option.value !== applied.id);

  // Pin it at the top of the first page only.
  if (page !== 1) return withoutApplied;

  // Respect the search term, if any.
  const normalizedTerm = term?.trim().toLowerCase();
  if (normalizedTerm) {
    const haystack = `${applied.name ?? ""} ${applied.code ?? ""}`.toLowerCase();
    if (!haystack.includes(normalizedTerm)) return withoutApplied;
  }

  return [adaptStructureToSelectOption(applied), ...withoutApplied];
};

/**
 * Whether a structure applied to a link can be pre-selected on entering
 * "Editar Programación". Requires a structure with at least one part — a
 * structure without parts has no stages to drive, so (CHS-2632, Opción 2) it is
 * treated as "no structure assigned" and the empty selector is kept.
 */
export const canPreselectStructure = (structure?: ProgrammingStructure | null): boolean =>
  !!structure && (structure.programmingStructureParts?.filter(Boolean).length ?? 0) > 0;

/**
 * Replaces preview placeholder IDs with stable UUIDs.
 * Sections with id starting with "preview-section-" get a new uuid.
 * Sessions with id starting with "preview-session-" get a new uuid.
 * Non-preview IDs (existing entities) are kept as-is.
 */
export const normalizePreviewIds = (
  sections: MicroprogrammingPreviewSection[],
): MicroprogrammingPreviewSection[] => {
  return (sections ?? []).map(section => ({
    ...section,
    id: section.id?.startsWith("preview-section-") ? uuid() : section.id,
    sessions: (section.sessions ?? []).map(session => ({
      ...session,
      id: session.id?.startsWith("preview-session-") ? uuid() : session.id,
    })),
  }));
};

interface LinkInfoForChangeset {
  id: string;
  course?: { id: string } | null;
  vacancies?: number | null;
}

/**
 * Transforms MicroprogrammingPreviewResult sections into a ProgrammingSessionCrudInput (v2 schema).
 *
 * - isPreview=false: existing section → each session uses sectionId
 * - isPreview=true: new section → each session uses componentId + courseId + vacancies
 *   (backend groups by (componentId, courseId) automatically; linkId comes from input.linkId global)
 * - deletes: all original section IDs (passed in) as { sections, sessions }, backend handles cascade
 */
export const adaptPreviewToChangeset = (
  sections: MicroprogrammingPreviewSection[],
  linkInfo: LinkInfoForChangeset,
  originalSectionIds: string[],
  programmingStructureId: string,
): ProgrammingSessionCrudInput => {
  const creates = (sections ?? []).flatMap(section => {
    const isNewSection = section.isPreview === true;

    return (section.sessions ?? []).map(session => ({
      sessionId: session.id,
      ...(isNewSection
        ? {
            componentId: section.component?.id ?? undefined,
            courseId: linkInfo.course?.id ?? undefined,
            vacancies: linkInfo.vacancies ?? 0,
          }
        : { sectionId: section.id }),
      changeBlocks: session.assignment?.blockRange
        ? {
            op: EditorOperation.Change,
            day: session.assignment.day ?? undefined,
            startTime: session.assignment.blockRange.start?.startingTime ?? undefined,
            endTime: session.assignment.blockRange.end?.endingTime ?? undefined,
          }
        : undefined,
      changeClassroom: {
        op: EditorOperation.Change,
        classroomIds: (session.classrooms ?? []).map(c => c.id),
      },
      changeInstructor: {
        op: EditorOperation.Change,
        instructorIds: (session.instructors ?? []).map(i => i.id),
      },
      changeIntervals: {
        op: EditorOperation.Change,
        intervalIds: (session.assignment?.intervals ?? []).map(i => i.id),
      },
    }));
  });

  return {
    linkId: linkInfo.id,
    editionMode: EditionMode.Programming,
    programmingStructureId,
    changesets: {
      deletes: {
        sections: originalSectionIds,
        sessions: [],
      },
      creates,
    },
  };
};

/**
 * The instructors unassigned in the Programming tab, for `unassignedInstructorIds` (CHS-2944).
 *
 * Every slot is compared with the loaded slot it comes from; split parts carry the original id
 * with a `_part<n>` suffix. Every slot is programmed, so none is left out.
 */
export const slotsUnassignedInstructorIds = (
  originalSlots: ProgrammingRow[],
  slots: ProgrammingRow[],
): string[] => {
  const originalsById = new Map((originalSlots ?? []).map(slot => [slot.id, slot]));

  return netUnassignedInstructorIds(
    (slots ?? []).map(slot => ({
      originalIds: (originalsById.get(slot.id.replace(/_part\d+$/, ""))?.instructors ?? []).map(
        instructor => instructor.id,
      ),
      finalIds: slot.instructors.map(instructor => instructor.id),
    })),
  );
};

/**
 * Flattens creates[], deletes[], and updates[] from the v2 sessionsCrud response
 * into a flat SessionPayload[] compatible with the result reducer.
 */
export const flattenStructuredCrudResponse = (
  crudResult: ProgrammingSessionCrudResult,
): SessionPayload[] => {
  if (!crudResult) return [];

  return [
    ...(crudResult?.payload?.creates ?? []),
    ...(crudResult?.payload?.deletes ?? []),
    ...(crudResult?.payload?.updates ?? []),
  ];
};
